import React, { useState, useRef, useEffect } from 'react';
import { Block } from '../classes/Block';
import {
  VideoSettings,
  RenderFormat,
  VIDEO_FORMATS,
  isVideoFormat,
} from './VideoSettings';
import { t } from '@lingui/core/macro';
import { useProject } from '../contexts/ProjectContext';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { ApiBlock, ApiCardGroup, ApiPayload } from '../utils/apiTypes';
import { Select, MenuItem, SelectChangeEvent, Menu } from '@mui/material';
import { exportToFormat, EXPORT_CONFIGS, ExportFormat, ExportLayout } from '../utils/exporterUtils';
import { CardGroup } from '../utils/cardGroups';
import { trackEvent } from '../statsig';
import { toFontUrlMap } from '../utils/fontHelpers';
import { buildCardsApiPayload, transformBlockToApiFormat } from '../utils/renderPayload';

interface UnifiedRenderProps {
  columnDefs: any[];
  sheetName: string;
  blocks: Block[];
  isEnabled: boolean;
  videoSettings: VideoSettings;
  cardGroups?: CardGroup[];
}

function getFormatDisplayName(format: RenderFormat): string {
  const displayNames: Record<RenderFormat, string> = {
    'H264': 'H264',
    'H265': 'H265',
    'ProRes_422_Proxy': 'ProRes 422 Proxy',
    'ProRes_422_LT': 'ProRes 422 LT',
    'ProRes_422': 'ProRes 422',
    'ProRes_422_HQ': 'ProRes 422 HQ',
    'ProRes_4444': 'ProRes 4444',
    'ProRes_4444_XQ': 'ProRes 4444 XQ',
    'PDF': 'PDF',
    'PNG': 'PNG',
    'TIFF': 'TIFF',
  };
  return displayNames[format] || format;
}

const createVideoRender = async (data: ApiPayload, projectId: string): Promise<{ status: string; render: { version: number } }> => {
  const response = await authFetch(`${getApiUrl()}/new-render?projectId=${projectId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    throw new Error('Failed to create render');
  }
  const result = await response.json();
  return result;
};

const UnifiedRender: React.FC<UnifiedRenderProps> = ({
  columnDefs,
  blocks,
  isEnabled,
  videoSettings,
  cardGroups = [],
}) => {
  const { selectedProject } = useProject();
  const initialSelectedFormat: RenderFormat = videoSettings.mode === 'cards' && videoSettings.video_format === 'PNG'
    ? 'PDF'
    : videoSettings.video_format;
  const [selectedFormat, setSelectedFormat] = useState<RenderFormat>(initialSelectedFormat);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [renderVersion, setRenderVersion] = useState<number | null>(null);
  const [tiffSplitCards, setTiffSplitCards] = useState<boolean>(false);
  const [pdfLayout, setPdfLayout] = useState<ExportLayout>('landscape');
  const [formatMenuOpen, setFormatMenuOpen] = useState<boolean>(false);
  const [tiffSubmenuPosition, setTiffSubmenuPosition] = useState<{ top: number; left: number } | null>(null);
  const popupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const keepFormatMenuOpenRef = useRef<boolean>(false);
  const isCardsMode: boolean = videoSettings.mode === 'cards';
  const effectiveSelectedFormat: RenderFormat = isCardsMode && selectedFormat === 'PNG'
    ? 'PDF'
    : selectedFormat;
  const isTiffSubmenuOpen: boolean = Boolean(tiffSubmenuPosition) && isCardsMode;

  const handleRender = async () => {
    if (!selectedProject) return;

    trackEvent('render_requested', {
      project_id: selectedProject.id,
      project_name: selectedProject.name,
      output_format: effectiveSelectedFormat,
      video_mode: videoSettings.mode,
      block_count: blocks.length,
      card_group_count: cardGroups.length,
      tiff_split_cards: tiffSplitCards,
      pdf_layout: pdfLayout,
    });
    setIsLoading(true);
    try {
      if (isVideoFormat(effectiveSelectedFormat)) {
        const renderData: ApiBlock[] | ApiCardGroup[] = isCardsMode
          ? buildCardsApiPayload({
            blocks,
            cardGroups,
            columnDefs,
            propertiesMode: 'render',
          })
          : blocks.map((block: Block) => transformBlockToApiFormat(block, {
            columnDefs,
            propertiesMode: 'render',
          }));

        const payload: ApiPayload = {
          video_settings: {
            background_color: videoSettings.background_color,
            fps_num: videoSettings.fps_num,
            resolution: videoSettings.resolution,
            transparent: videoSettings.transparent,
            video_time: videoSettings.video_time,
            aspect_ratio: videoSettings.aspect_ratio,
            video_format: effectiveSelectedFormat,
            fonts: JSON.stringify(toFontUrlMap(videoSettings.fonts)),
            logos: JSON.stringify(videoSettings.logos),
            mode: videoSettings.mode,
            ...(isCardsMode ? { card_framing: videoSettings.card_framing } : {}),
          },
          data: renderData,
        };

        const response = await createVideoRender(payload, selectedProject.id);

        if (!response || !response.render || typeof response.render.version !== 'number') {
          throw new Error('Invalid response from server: missing version number');
        }

        const version: number = response.render.version;
        trackEvent('render_started', {
          project_id: selectedProject.id,
          output_format: effectiveSelectedFormat,
          render_version: version,
          video_mode: videoSettings.mode,
        });
        setRenderVersion(version);
        setShowPopup(true);

        if (popupTimeoutRef.current) {
          clearTimeout(popupTimeoutRef.current);
        }

        popupTimeoutRef.current = setTimeout(() => {
          setShowPopup(false);
          popupTimeoutRef.current = null;
        }, 5000);

      } else {
        // Image export - call /export-png endpoint with format parameter
        const formatLower = effectiveSelectedFormat.toLowerCase() as ExportFormat;
        const config = EXPORT_CONFIGS[formatLower];

        await exportToFormat({
          projectId: selectedProject.id,
          projectName: selectedProject.name ?? '',
          blocks,
          cardGroups,
          columnDefs,
          videoSettings,
          config,
          transparent: false,
          tiffSplitCards,
          layout: isCardsMode && effectiveSelectedFormat === 'PDF' ? pdfLayout : undefined,
        });
        trackEvent('export_completed', {
          project_id: selectedProject.id,
          output_format: effectiveSelectedFormat,
          video_mode: videoSettings.mode,
          tiff_split_cards: tiffSplitCards,
          pdf_layout: pdfLayout,
        });
      }
    } catch (error) {
      console.error('Error during render:', error);
      trackEvent('render_failed', {
        project_id: selectedProject.id,
        output_format: effectiveSelectedFormat,
        video_mode: videoSettings.mode,
      });
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to render: ${errorMessage}\nPlease try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormatChange = (e: SelectChangeEvent<string>) => {
    const nextFormat: RenderFormat = e.target.value as RenderFormat;
    setTiffSubmenuPosition(null);
    if (isCardsMode && nextFormat === 'PNG') {
      setSelectedFormat('PDF');
      return;
    }
    if (isCardsMode && nextFormat === 'TIFF') {
      setTiffSplitCards(true);
    }
    trackEvent('render_format_changed', {
      project_id: selectedProject?.id,
      output_format: nextFormat,
      video_mode: videoSettings.mode,
    });
    setSelectedFormat(nextFormat);
  };

  const handleFormatMenuClose = (): void => {
    if (keepFormatMenuOpenRef.current) {
      keepFormatMenuOpenRef.current = false;
      return;
    }
    setTiffSubmenuPosition(null);
    setFormatMenuOpen(false);
  };

  const handleTiffSubmenuClose = (): void => {
    setTiffSubmenuPosition(null);
  };

  const handleTiffMenuButtonMouseDown = (event: React.MouseEvent<HTMLButtonElement>): void => {
    keepFormatMenuOpenRef.current = true;
    event.preventDefault();
    event.stopPropagation();
  };

  const handleTiffMenuButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    if (!isCardsMode) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const buttonRect: DOMRect = event.currentTarget.getBoundingClientRect();
    window.setTimeout(() => {
      setTiffSubmenuPosition({
        top: Math.round(buttonRect.top),
        left: Math.round(buttonRect.right + 6),
      });
    }, 0);
  };

  const handlePdfLayoutSelect = (layout: ExportLayout): void => {
    setPdfLayout(layout);
    trackEvent('render_format_changed', {
      project_id: selectedProject?.id,
      output_format: 'PDF',
      video_mode: videoSettings.mode,
      pdf_layout: layout,
    });
    setSelectedFormat('PDF');
    setFormatMenuOpen(false);
  };

  const handleTiffVariantSelect = (splitCards: boolean): void => {
    setTiffSplitCards(splitCards);
    trackEvent('render_format_changed', {
      project_id: selectedProject?.id,
      output_format: 'TIFF',
      video_mode: videoSettings.mode,
      tiff_split_cards: splitCards,
    });
    setSelectedFormat('TIFF');
    setFormatMenuOpen(false);
    handleTiffSubmenuClose();
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
      }
    };
  }, []);

  const isVideo: boolean = isVideoFormat(effectiveSelectedFormat);
  const buttonText = isLoading
    ? (isVideo ? t`Rendering...` : t`Exporting...`)
    : t`Render`;

  return (
    <>
      <div className="unified-render" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Select
          value={effectiveSelectedFormat}
          onChange={handleFormatChange}
          open={formatMenuOpen}
          onOpen={() => {
            setFormatMenuOpen(true);
            setTiffSubmenuPosition(null);
          }}
          onClose={handleFormatMenuClose}
          style={{ width: '180px', height: '36px', fontSize: '14px' }}
          sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
          renderValue={(value) => (
            isCardsMode && value === 'PDF'
              ? `PDF ${pdfLayout === 'landscape' ? 'Landscape' : 'Portrait'}`
              : getFormatDisplayName(value as RenderFormat)
          )}
        >
          <MenuItem disabled style={{ opacity: 0.6, fontWeight: 600, fontSize: '12px', marginTop: '8px' }}>
            {t`Image Formats`}
          </MenuItem>
          {isCardsMode ? [
            <MenuItem key="pdf-landscape" value="PDF" onClick={() => handlePdfLayoutSelect('landscape')}>
              PDF Landscape
            </MenuItem>,
            <MenuItem key="pdf-portrait" value="PDF" onClick={() => handlePdfLayoutSelect('portrait')}>
              PDF Portrait
            </MenuItem>,
          ] : (
            <MenuItem value="PDF">PDF</MenuItem>
          )}
          <MenuItem value="PNG" disabled={isCardsMode}>PNG</MenuItem>
          <MenuItem
            value="TIFF"
            className={isCardsMode ? 'tiff-menu-item' : undefined}
          >
            <span>TIFF</span>
            {isCardsMode ? (
              <button
                type="button"
                className="tiff-menu-more-button"
                onClick={handleTiffMenuButtonClick}
                onMouseDown={handleTiffMenuButtonMouseDown}
                aria-haspopup="menu"
                aria-expanded={isTiffSubmenuOpen}
                aria-label="Open TIFF export options"
              >
                <span className="tiff-menu-more-dot" />
                <span className="tiff-menu-more-dot" />
                <span className="tiff-menu-more-dot" />
              </button>
            ) : null}
          </MenuItem>

          {/* Video Formats */}
          <MenuItem disabled style={{ opacity: 0.6, fontWeight: 600, fontSize: '12px' }}>
            {t`Video Formats`}
          </MenuItem>
          {VIDEO_FORMATS.map((format) => (
            <MenuItem key={format} value={format}>
              {getFormatDisplayName(format)}
            </MenuItem>
          ))}
          
          {/* Divider */}
        </Select>

        <Menu
          anchorReference="anchorPosition"
          anchorPosition={tiffSubmenuPosition ?? undefined}
          open={isTiffSubmenuOpen}
          onClose={handleTiffSubmenuClose}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          MenuListProps={{
            className: 'tiff-submenu-list',
            onMouseLeave: handleTiffSubmenuClose,
          }}
          disableAutoFocus
        >
          <MenuItem onClick={() => handleTiffVariantSelect(false)}>
            One tiff with all the cards
          </MenuItem>
          <MenuItem onClick={() => handleTiffVariantSelect(true)}>
            A zip with a tiff for each card
          </MenuItem>
        </Menu>

        <button
          onClick={handleRender}
          disabled={!isEnabled || isLoading}
          className="export-button"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            position: 'relative'
          }}
        >
          {isLoading && (
            <div
              className="loading-spinner"
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
          )}
          {buttonText}
        </button>

        {/* Tooltip Icon */}
        <div className="tooltip-wrapper" style={{ paddingLeft: '3px' }}>
          <span className="tooltip-icon">?</span>
          <span className="tooltip-text" style={{ paddingBottom: '2px' }}>
            {isVideo
              ? t`Render`
              : t`Export your credits as an image file`
            }
          </span>
        </div>
      </div>

      {/* Popup notification for video renders */}
      {showPopup && renderVersion !== null && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#319204',
            color: 'white',
            padding: '20px 40px',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 'bold',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
            textAlign: 'center'
          }}
        >
          Render version {renderVersion} started
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default UnifiedRender;
